import { useCallback, useEffect, useState } from "react";
import { Compass, Gauge, MapPin, Route, ShieldCheck, SlidersHorizontal } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const WEIGHTS = [
  ["added_travel", "Added drive time"],
  ["backtrack", "Backtracking"],
  ["gap", "Idle gaps"],
  ["lateness", "Running-late risk"],
  ["isolation", "Geographic island"],
];

const NUMBERS = [
  ["max_insertion_minutes", "Max added driving (min)", "A day is not offered publicly when the best insertion costs more than this"],
  ["options_per_day", "Options shown per day", "How many route-efficient times a customer sees for each day"],
  ["travel_buffer_minutes", "Travel buffer (min)", "Slack required on top of the drive between two jobs"],
  ["isolation_km", "Isolation distance (km)", "Beyond this from the nearest job that day, a visit counts as an island"],
  ["backtrack_ratio", "Backtrack ratio", "Detour length ÷ direct length above which the insertion is penalised"],
  ["free_idle_minutes", "Free idle allowance (min)", "Idle time ignored before the gap penalty starts"],
];

const errText = (e) => {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).filter(Boolean).join("; ");
  return e.message || "Request failed";
};

function Card({ title, icon: Icon, children, testId, note }) {
  return (
    <section className="rounded-2xl bg-white border border-slate-200 p-5 mb-6" data-testid={testId}>
      <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wider mb-1 flex items-center gap-1.5">
        {Icon ? <Icon className="w-4 h-4" /> : null} {title}
      </h2>
      {note ? <p className="text-xs text-slate-500 mb-4">{note}</p> : <div className="mb-3" />}
      {children}
    </section>
  );
}

function Settings({ data, onSaved }) {
  const [form, setForm] = useState(data.config);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(data.config), [data.config]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setWeight = (k, v) => setForm((f) => ({ ...f, weights: { ...f.weights, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.put("/admin/geo-routing/config", {
        enabled: form.enabled,
        travel_provider: form.travel_provider,
        max_insertion_minutes: Number(form.max_insertion_minutes),
        options_per_day: Number(form.options_per_day),
        travel_buffer_minutes: Number(form.travel_buffer_minutes),
        isolation_km: Number(form.isolation_km),
        backtrack_ratio: Number(form.backtrack_ratio),
        free_idle_minutes: Number(form.free_idle_minutes),
        road_factor: Number(form.road_factor),
        weights: Object.fromEntries(WEIGHTS.map(([k]) => [k, Number(form.weights[k])])),
        depot: form.depot,
      });
      toast.success("Route scoring saved");
      onSaved(res.config);
    } catch (e) {
      toast.error(errText(e));
    } finally { setSaving(false); }
  };

  return (
    <Card title="Route scoring" icon={SlidersHorizontal} testId="geo-settings-card"
      note="Eligibility always comes first — these weights only decide which of the technically free times a customer is offered. Lower score wins.">
      <div className="flex flex-wrap items-center gap-4 mb-5">
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <input type="checkbox" data-testid="geo-enabled" checked={!!form.enabled}
            onChange={(e) => set("enabled", e.target.checked)} className="w-4 h-4" />
          GEO-aware availability on
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          Travel times
          <select data-testid="geo-provider" value={form.travel_provider}
            onChange={(e) => set("travel_provider", e.target.value)}
            className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm">
            <option value="road_model">Road model (no API key)</option>
            <option value="google_routes">Google Routes API</option>
          </select>
        </label>
        <span className={`text-[11px] font-bold uppercase rounded-full px-2 py-0.5 ${data.google_key_configured ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
          data-testid="geo-key-status">
          {data.google_key_configured ? "Google server key present" : "No Google server key — road model in use"}
        </span>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
        {NUMBERS.map(([k, label, hint]) => (
          <label key={k} className="block">
            <span className="block text-xs font-semibold text-slate-700 mb-1">{label}</span>
            <input type="number" step="0.1" data-testid={`geo-${k}`} value={form[k]}
              onChange={(e) => set(k, e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
            <span className="block text-[11px] text-slate-500 mt-1">{hint}</span>
          </label>
        ))}
      </div>

      <div className="grid sm:grid-cols-5 gap-3 mb-5">
        {WEIGHTS.map(([k, label]) => (
          <label key={k} className="block">
            <span className="block text-xs font-semibold text-slate-700 mb-1">{label}</span>
            <input type="number" step="0.1" min="0" data-testid={`geo-weight-${k}`}
              value={form.weights?.[k] ?? 0} onChange={(e) => setWeight(k, e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
          </label>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="block text-xs font-semibold text-slate-700 mb-1">Depot label</span>
          <input data-testid="geo-depot-label" value={form.depot?.label || ""}
            onChange={(e) => set("depot", { ...form.depot, label: e.target.value })}
            className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
        </label>
        <label className="block">
          <span className="block text-xs font-semibold text-slate-700 mb-1">Depot latitude</span>
          <input data-testid="geo-depot-lat" value={form.depot?.lat ?? ""}
            onChange={(e) => set("depot", { ...form.depot, lat: e.target.value })}
            className="w-32 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
        </label>
        <label className="block">
          <span className="block text-xs font-semibold text-slate-700 mb-1">Depot longitude</span>
          <input data-testid="geo-depot-lng" value={form.depot?.lng ?? ""}
            onChange={(e) => set("depot", { ...form.depot, lng: e.target.value })}
            className="w-32 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
        </label>
        <button data-testid="geo-save" onClick={save} disabled={saving}
          className="rounded-xl bg-cyan-700 px-4 py-2 text-sm font-bold text-white hover:bg-cyan-800 disabled:opacity-50">
          {saving ? "Saving…" : "Save scoring"}
        </button>
      </div>
      {form.travel_provider_error ? (
        <p className="mt-3 text-xs text-rose-600" data-testid="geo-provider-error">
          Last travel-provider error: {form.travel_provider_error}
        </p>
      ) : form.travel_provider_last_error_at ? (
        <p className="mt-3 text-xs text-slate-400" data-testid="geo-provider-healthy">
          Healthy — recovered from a travel-provider error last seen{" "}
          {new Date(form.travel_provider_last_error_at).toLocaleString()}.
        </p>
      ) : null}
    </Card>
  );
}

function Readiness({ data, onSaved }) {
  const [audit, setAudit] = useState(null);
  const [policy, setPolicy] = useState(data.config.unmapped_technician_policy);

  useEffect(() => {
    api.get("/admin/geo-routing/staff-mapping-audit")
      .then(({ data: d }) => setAudit(d)).catch(() => {});
  }, []);

  const savePolicy = async (value) => {
    setPolicy(value);
    try {
      const { data: res } = await api.put("/admin/geo-routing/config",
        { unmapped_technician_policy: value });
      toast.success("Unmapped-technician rule saved");
      onSaved(res.config);
    } catch (e) { toast.error(errText(e)); }
  };

  const active = data.config.public_ranking_active;
  return (
    <Card title="Production readiness" icon={ShieldCheck} testId="geo-readiness-card"
      note="Public ranking stays off until GEO_PUBLIC_SLOT_RANKING_ENABLED is set on the server AND the switch above is on. Admin tools keep working either way.">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase ${active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}
          data-testid="geo-gate-status">
          Public ranking {active ? "ACTIVE" : "not activated"}
        </span>
        <span className="text-xs text-slate-500">
          server flag GEO_PUBLIC_SLOT_RANKING_ENABLED ={" "}
          <b>{String(!!data.config.public_ranking_env_enabled)}</b>
        </span>
        <label className="flex items-center gap-2 text-sm text-slate-700 ml-auto">
          Technician with no ServiceM8 mapping
          <select data-testid="geo-unmapped-policy" value={policy}
            onChange={(e) => savePolicy(e.target.value)}
            className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm">
            <option value="exclude">Exclude from ranking (recommended)</option>
            <option value="legacy">Fall back to the old availability list</option>
            <option value="allow">Rank anyway, AWARE bookings only</option>
          </select>
        </label>
      </div>

      {audit ? (
        <>
          <div className="flex flex-wrap gap-2 mb-3 text-[11px]">
            <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-700" data-testid="geo-mapping-counts">
              Bookable technicians {audit.counts.bookable} · mapped {audit.counts.bookable_mapped} · unmapped {audit.counts.bookable_unmapped} · publicly eligible {audit.counts.public_booking_eligible}
            </span>
            {audit.servicem8_staff_read_error ? (
              <span className="rounded-full bg-rose-100 px-2 py-0.5 font-semibold text-rose-700">
                ServiceM8 staff read failed
              </span>
            ) : null}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="geo-mapping-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">AWARE technician</th>
                  <th className="py-2 pr-3">AWARE staff ID</th>
                  <th className="py-2 pr-3">ServiceM8 staff</th>
                  <th className="py-2 pr-3">ServiceM8 UUID</th>
                  <th className="py-2 pr-3">Mapping</th>
                  <th className="py-2 pr-3">Eligible for</th>
                  <th className="py-2 pr-3">Public booking</th>
                </tr>
              </thead>
              <tbody>
                {audit.technicians.map((t) => (
                  <tr key={t.aware_staff_id} className="border-b border-slate-100 align-top"
                    data-testid={`geo-mapping-row-${t.aware_staff_id}`}>
                    <td className="py-2 pr-3">
                      {t.aware_name}
                      <span className="block text-[11px] text-slate-500">{t.aware_email} · {t.account_state} · {t.rostered_days} rostered days</span>
                    </td>
                    <td className="py-2 pr-3 font-mono text-[11px]">{t.aware_staff_id}</td>
                    <td className="py-2 pr-3">{t.servicem8_staff_name || <span className="italic text-slate-400">—</span>}</td>
                    <td className="py-2 pr-3 font-mono text-[11px]">{t.servicem8_staff_uuid || "—"}</td>
                    <td className="py-2 pr-3">
                      {t.mapping_confirmed ? (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700">Confirmed</span>
                      ) : (
                        <>
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-700">Not mapped</span>
                          <span className="block text-[11px] text-slate-500 mt-1">
                            {t.suggestion?.basis?.replaceAll("_", " ")}
                            {t.suggestion?.sm8_staff_name ? ` → ${t.suggestion.sm8_staff_name} (${t.suggestion.sm8_staff_email || "no email"})` : ""}
                          </span>
                        </>
                      )}
                      {t.mapping_status === "DUPLICATE" ? (
                        <span className="block text-[11px] text-rose-600 mt-1">
                          Duplicate with: {t.duplicate_with.join(", ")}
                        </span>
                      ) : null}
                    </td>
                    <td className="py-2 pr-3 text-[11px] text-slate-600">
                      {t.eligible_inspection_types.length
                        ? t.eligible_inspection_types.map((x) => x.replaceAll("_", " ")).join(", ")
                        : <span className="italic text-slate-400">not rostered</span>}
                    </td>
                    <td className="py-2 pr-3" data-testid={`geo-public-eligible-${t.aware_staff_id}`}>
                      {t.public_booking_eligible ? (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700">Eligible</span>
                      ) : (
                        <>
                          <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold uppercase text-rose-700">Excluded</span>
                          <span className="block text-[11px] text-slate-500 mt-1">
                            {t.blocker_reason?.replaceAll("_", " ")}
                          </span>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-[11px] text-slate-500">
            Mappings are confirmed in Settings → ServiceM8. Suggestions are never applied
            automatically — a matching name with a different email is reported, not assumed.
            {audit.mapping_enforced_for_public_booking
              ? " A valid, unique, resolving ServiceM8 mapping is currently REQUIRED for public booking eligibility (availability provider = ServiceM8)."
              : " ServiceM8 mapping is not currently enforced for public booking (availability provider = AWARE only)."}
          </p>
        </>
      ) : null}
    </Card>
  );
}

function Probe() {
  const [q, setQ] = useState({ postcode: "4563", suburb: "", lat: "", lng: "" });
  const [all, setAll] = useState(false);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      // Blank fields are omitted — an empty lat/lng is not a number.
      const params = { include_all: all, purpose: "health_check" };
      Object.entries(q).forEach(([k, v]) => {
        if (String(v || "").trim()) params[k] = String(v).trim();
      });
      const { data } = await api.get("/admin/geo-routing/slot-scores", { params });
      setRes(data);
      if (!data.applied) toast.error(`No ranking: ${data.reason}`);
    } catch (e) {
      toast.error(errText(e));
    } finally { setBusy(false); }
  };

  return (
    <Card title="Why did this slot rank here?" icon={Compass} testId="geo-probe-card"
      note="Score a real property address the way the booking page does. Customers never see any of this — they only get the top options.">
      <div className="flex flex-wrap items-end gap-2 mb-4">
        {[["postcode", "Postcode"], ["suburb", "Suburb"], ["lat", "Latitude"], ["lng", "Longitude"]].map(([k, label]) => (
          <label key={k} className="block">
            <span className="block text-xs font-semibold text-slate-700 mb-1">{label}</span>
            <input data-testid={`geo-probe-${k}`} value={q[k]}
              onChange={(e) => setQ({ ...q, [k]: e.target.value })}
              className="w-32 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
          </label>
        ))}
        <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 pb-2">
          <input type="checkbox" data-testid="geo-probe-all" checked={all}
            onChange={(e) => setAll(e.target.checked)} className="w-4 h-4" />
          Show every technically free time (override view)
        </label>
        <button data-testid="geo-probe-run" onClick={run} disabled={busy}
          className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50">
          {busy ? "Scoring…" : "Score address"}
        </button>
      </div>

      {res?.applied ? (
        <>
          <div className="flex flex-wrap gap-2 mb-3 text-[11px]">
            <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-700" data-testid="geo-probe-precision">
              Address: {res.address_precision.replace("_", " ")}
            </span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-700">
              Travel: {res.travel_provider === "google_routes" ? "Google Routes" : "road model"}
              {res.travel_source && res.travel_source !== res.travel_provider
                ? ` (fallback: ${res.travel_source})` : ""}
            </span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-700">
              ServiceM8 techs mapped: {res.sm8_techs_mapped}
            </span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-700">
              Candidates: {res.candidates.length}
            </span>
            {res.servicem8?.error ? (
              <span className="rounded-full bg-rose-100 px-2 py-0.5 font-semibold text-rose-700">
                ServiceM8 read error — scored on AWARE bookings only
              </span>
            ) : null}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="geo-candidates-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Time</th>
                  <th className="py-2 pr-3">Technician</th>
                  <th className="py-2 pr-3">Score</th>
                  <th className="py-2 pr-3">Added drive</th>
                  <th className="py-2 pr-3">Fits between</th>
                  <th className="py-2 pr-3">Technician day</th>
                  <th className="py-2 pr-3">Why</th>
                </tr>
              </thead>
              <tbody>
                {res.candidates.map((c) => (
                  <tr key={`${c.slot_id}-${c.start_time}`} className="border-b border-slate-100 align-top"
                    data-testid={`geo-candidate-${c.date}-${c.start_time}-${c.technician_id || "none"}`}>
                    <td className="py-2 pr-3 whitespace-nowrap">{c.date}</td>
                    <td className="py-2 pr-3 font-semibold whitespace-nowrap">{c.start_time}</td>
                    <td className="py-2 pr-3">{c.technician_name}</td>
                    <td className="py-2 pr-3">
                      <span className={`font-bold ${c.score <= 15 ? "text-emerald-700" : c.score <= 35 ? "text-amber-600" : "text-rose-600"}`}>
                        {c.score}
                      </span>
                      <span className="block text-[10px] text-slate-500">
                        {Object.entries(c.components).filter(([, v]) => v > 0)
                          .map(([k, v]) => `${k.replace("_", " ")} ${v}`).join(" · ") || "no penalties"}
                      </span>
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap">+{c.added_minutes} min · {c.added_km} km</td>
                    <td className="py-2 pr-3 text-xs text-slate-600">{c.previous_stop} → {c.next_stop}</td>
                    <td className="py-2 pr-3 text-xs text-slate-600" data-testid={`geo-day-${c.slot_id}-${c.start_time}`}>
                      {c.empty_day ? <span className="italic text-slate-400">no jobs yet</span>
                        : (c.day_stops || []).map((s) => `${s.start} ${s.label}${s.source === "servicem8" ? " (SM8)" : ""}`).join(" · ")}
                      <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase ${c.servicem8_day_read ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"}`}>
                        {c.servicem8_day_read ? "SM8 read" : "not mapped"}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-xs text-slate-600">
                      {c.offered_publicly ? (
                        <span className="mr-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700">Offered</span>
                      ) : c.feasible ? (
                        <span className="mr-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-600">Held back</span>
                      ) : (
                        <span className="mr-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold uppercase text-rose-700">
                          {(c.excluded_reason || "").replaceAll("_", " ")}
                        </span>
                      )}
                      {c.reasons.join("; ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </Card>
  );
}

function Anchors() {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState({});

  const load = useCallback(() => {
    api.get("/admin/geo-routing/technicians").then(({ data: d }) => setData(d)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const save = async (tid) => {
    const e = edit[tid] || {};
    try {
      await api.put(`/admin/geo-routing/technicians/${tid}/anchors`, {
        start: e.start_postcode || e.start_label
          ? { label: e.start_label, postcode: e.start_postcode, state: "QLD" } : null,
        end: e.end_postcode || e.end_label
          ? { label: e.end_label, postcode: e.end_postcode, state: "QLD" } : null,
      });
      toast.success("Route anchors saved");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  if (!data) return null;
  return (
    <Card title="Technician start / end points" icon={MapPin} testId="geo-anchors-card"
      note={`Used when a day is empty or the new visit would be first or last. Blank means the depot (${data.depot?.label}). Never shown to customers.`}>
      <div className="space-y-3">
        {data.technicians.map((t) => {
          const e = edit[t.id] || {};
          return (
            <div key={t.id} className="flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2"
              data-testid={`geo-anchor-row-${t.id}`}>
              <span className="text-sm font-semibold text-slate-800 w-40">{t.name}</span>
              <span className="text-xs text-slate-500 w-44">
                Start: {t.route_start_location?.label || data.depot?.label}<br />
                End: {t.route_end_location?.label || t.route_start_location?.label || data.depot?.label}
              </span>
              <input placeholder="Start label" data-testid={`geo-anchor-start-label-${t.id}`}
                value={e.start_label ?? (t.route_start_location?.label || "")}
                onChange={(ev) => setEdit({ ...edit, [t.id]: { ...e, start_label: ev.target.value } })}
                className="w-36 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
              <input placeholder="Start postcode" data-testid={`geo-anchor-start-postcode-${t.id}`}
                value={e.start_postcode ?? ""}
                onChange={(ev) => setEdit({ ...edit, [t.id]: { ...e, start_postcode: ev.target.value } })}
                className="w-28 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
              <input placeholder="End label" data-testid={`geo-anchor-end-label-${t.id}`}
                value={e.end_label ?? (t.route_end_location?.label || "")}
                onChange={(ev) => setEdit({ ...edit, [t.id]: { ...e, end_label: ev.target.value } })}
                className="w-36 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
              <input placeholder="End postcode" data-testid={`geo-anchor-end-postcode-${t.id}`}
                value={e.end_postcode ?? ""}
                onChange={(ev) => setEdit({ ...edit, [t.id]: { ...e, end_postcode: ev.target.value } })}
                className="w-28 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
              <button data-testid={`geo-anchor-save-${t.id}`} onClick={() => save(t.id)}
                className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-bold text-white hover:bg-slate-800">
                Save
              </button>
            </div>
          );
        })}
        {data.technicians.length === 0 && (
          <div className="text-xs text-slate-400 italic">No active technicians</div>
        )}
      </div>
    </Card>
  );
}

function Metrics() {
  const [m, setM] = useState(null);
  useEffect(() => {
    api.get("/admin/geo-routing/metrics", { params: { days: 30 } })
      .then(({ data }) => setM(data)).catch(() => {});
  }, []);
  if (!m) return null;
  const tiles = [
    ["Bookings scored", `${m.scored} of ${m.bookings}`],
    ["Avg added driving", m.avg_added_minutes == null ? "—" : `${m.avg_added_minutes} min`],
    ["Avg added distance", m.avg_added_km == null ? "—" : `${m.avg_added_km} km`],
    ["Avg route score", m.avg_route_score ?? "—"],
    ["Avg saving vs worst slot", m.avg_minutes_saved_vs_worst == null ? "—" : `${m.avg_minutes_saved_vs_worst} min`],
    ["Staff overrides", `${m.overrides}`],
  ];
  return (
    <Card title="Scheduling efficiency · last 30 days" icon={Gauge} testId="geo-metrics-card"
      note="Recorded on every booking so the saving can be measured over time.">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {tiles.map(([label, value]) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className="text-lg font-bold text-slate-900">{value}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function AdminGeoRouting() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/geo-routing/config").then(({ data: d }) => setData(d)).catch(() => {});
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="admin-geo-routing-page">
      <header className="mb-6">
        <h1 className="text-2xl font-black text-slate-900 flex items-center gap-2">
          <Route className="w-6 h-6" /> GEO-aware scheduling
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl">
          Customers are offered the appointment times that create the most efficient
          run for whichever eligible technician is closest to their property — not
          every time that happens to be free. ServiceM8 remains the operational
          schedule; staff can always override.
        </p>
      </header>
      {data ? <Settings data={data} onSaved={(config) => setData({ ...data, config })} /> : null}
      {data ? <Readiness data={data} onSaved={(config) => setData({ ...data, config })} /> : null}
      <Probe />
      <Anchors />
      <Metrics />
    </div>
  );
}
